import { latLngToLocalMeters, localMetersToLatLng } from '@roadworld/map-generator';
import type { LatLng } from '@roadworld/shared';

/**
 * A session-fixed equirectangular origin. All chunks in a session share this origin so
 * their world coordinates form a consistent frame. Valid for ~5 km radius without
 * appreciable distortion at most latitudes.
 */
export interface SessionFrame {
  origin: LatLng;
  chunkSize: number;
}

export interface ChunkCoord {
  cx: number;
  cz: number;
}

/** World XZ for the center of a chunk. Chunk (0,0) is at world (0,0). */
export function chunkCenterWorld(c: ChunkCoord, chunkSize: number): { x: number; z: number } {
  return { x: c.cx * chunkSize, z: c.cz * chunkSize };
}

/** Lat/lng for the center of a chunk. */
export function chunkCenterLatLng(c: ChunkCoord, frame: SessionFrame): LatLng {
  const { x, z } = chunkCenterWorld(c, frame.chunkSize);
  const { lat, lng } = localMetersToLatLng(x, z, frame.origin.lat, frame.origin.lng);
  return { lat, lng };
}

/** Which chunk does a world XZ point belong to? Chunks are square, centered on grid points. */
export function worldToChunkCoord(x: number, z: number, chunkSize: number): ChunkCoord {
  return { cx: Math.round(x / chunkSize), cz: Math.round(z / chunkSize) };
}

/** Convert any lat/lng to world XZ relative to the session origin. */
export function latLngToWorld(latLng: LatLng, frame: SessionFrame): { x: number; z: number } {
  return latLngToLocalMeters(latLng.lat, latLng.lng, frame.origin.lat, frame.origin.lng);
}

/** Inverse of {@link latLngToWorld} — convert world XZ back to lat/lng. */
export function worldToLatLng(x: number, z: number, frame: SessionFrame): LatLng {
  return localMetersToLatLng(x, z, frame.origin.lat, frame.origin.lng);
}

export function chunkKey(c: ChunkCoord): string {
  return `${c.cx},${c.cz}`;
}

/** Enumerate all chunk coords within `radius` of the given center (Chebyshev distance). */
export function chunksInRing(center: ChunkCoord, radius: number): ChunkCoord[] {
  const out: ChunkCoord[] = [];
  for (let dz = -radius; dz <= radius; dz++) {
    for (let dx = -radius; dx <= radius; dx++) {
      out.push({ cx: center.cx + dx, cz: center.cz + dz });
    }
  }
  return out;
}

export function chebyshev(a: ChunkCoord, b: ChunkCoord): number {
  return Math.max(Math.abs(a.cx - b.cx), Math.abs(a.cz - b.cz));
}
